import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddToListMode } from "@/enum/list.enum";
import { SelectionUnit, transferResultMessage } from "@/utils/transferResultMessage";

import { AddToListConfirmModal, PendingTransfer } from "./AddToListConfirmModal";

const RESULT_MESSAGE_MS = 6000;

/** What an "add to list" answered: the people it added and those the list already held. */
export interface AddToListAnswer {
	count?: number;
	skipped?: number;
}

export type AddToListTarget = { listId: number } | { listName: string };

export type AddToListSource = "contacts" | "companies";

interface UseAddToListConfirmArgs {
	/**
	 * How many the selection covers (for the confirm question), and which table it came from:
	 * Contacts adds contacts and queues their lead scoring; Companies and Competitors count
	 * companies, add their contacts and queue nothing.
	 */
	selection: { count: number; source: AddToListSource };
	/** Sends the selection to the picked list and resolves with the server's answer. */
	send: (target: AddToListTarget) => Promise<AddToListAnswer>;
	showMessage: (text: string, autoHideDuration?: number) => void;
}

export interface UseAddToListConfirmResult {
	onSelectList: (list: { id: number; name: string }) => void;
	onCreateNew: (name: string) => void;
	/** The confirmation dialog, or null while nothing is picked. */
	modal: ReactNode;
}

/**
 * "Add to List" from the Contacts, Companies and Competitors tables: pick a list, confirm, and
 * report what the server did. Each table said "Adding {selection} contacts", while the server adds
 * only the people the list does not hold yet (and for companies, their people, not the companies),
 * so the message is built from its answer (PREC-4595).
 */
export const useAddToListConfirm = ({
	selection,
	send,
	showMessage,
}: UseAddToListConfirmArgs): UseAddToListConfirmResult => {
	const { t } = useTranslation();
	const [pending, setPending] = useState<PendingTransfer | null>(null);
	const selectionUnit: SelectionUnit = selection.source;

	const onSelectList = useCallback((list: { id: number; name: string }) => {
		setPending({ type: "existing", listId: list.id, listName: list.name });
	}, []);

	const onCreateNew = useCallback((name: string) => {
		setPending({ type: "new", listName: name });
	}, []);

	const confirm = async () => {
		if (!pending) {
			return;
		}

		try {
			// A picked list without an id must not fall through to "create a list of that name".
			if (pending.type === "existing" && !pending.listId) {
				throw new Error("The picked list has no id");
			}

			const answer = await send(
				pending.listId ? { listId: pending.listId } : { listName: pending.listName }
			);

			showMessage(
				transferResultMessage(t, {
					mode: AddToListMode.COPY,
					listName: pending.listName,
					isNewList: pending.type === "new",
					selectionUnit,
					processingNote: selection.source === "contacts",
					result: { added: answer?.count, skipped: answer?.skipped },
				}),
				RESULT_MESSAGE_MS
			);
		} catch {
			showMessage(t("basics.error"));
		} finally {
			setPending(null);
		}
	};

	const modal = pending ? (
		<AddToListConfirmModal
			pending={pending}
			selectionCount={selection.count}
			selectionUnit={selectionUnit}
			onClose={() => setPending(null)}
			onConfirm={confirm}
		/>
	) : null;

	return { onSelectList, onCreateNew, modal };
};
